import React from 'react';
import {
  CalendarOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  LeftOutlined,
  MailOutlined,
  MinusOutlined,
  PlusOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  AttendeesInfoValidator,
  GuestWaitingList,
  LookEntity,
} from '@seaters/constants';
import {
  useGuestCancelWaitingList,
  useSendGuestIxpoleTicketLink,
  useUpdateGuestAttendeeInfo,
  useUpdateGuestWaitingListSeats,
} from '@seaters/data-access';
import { baseColor600, LexicalEditor } from '@seaters/ui';
import {
  Alert,
  Button,
  Divider,
  Form,
  Input,
  notification,
  Popconfirm,
} from 'antd';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// apps/invitation has no vite-plugin-svgr, so this `?react` import compiles to an
// asset URL rather than a component — the banner fallback logo has never rendered.
// @seaters/ui's `Logo` breaks identically: packages/ui is consumed as source, so its
// own `?react` imports are transformed by this app's Vite too. The barrel is not an
// escape route until svgr is added here.
// eslint-disable-next-line no-restricted-imports -- pending svgr in apps/invitation
import SeatersLogoLight from '@seaters/ui/src/lib/assets/icons/logo_seaters_light.svg?react';
import { CalendarSelection } from './CalendarSelection';
import {
  getIxpoleGuestSaveErrorTranslationKey,
  getCurrentSeatCount,
  getInitialSelectedTickets,
  getMaxSelectableTickets,
} from './ixpoleGuestFlow.utils';
import { decodeAmpHtmlEntity, getBrandColors, getLocale } from './utils';
import styles from './IxpoleGuestFlow.module.css';

dayjs.extend(LocalizedFormat);

type IxpoleGuestFlowProps = {
  fanGroupData: LookEntity;
  waitingListData: GuestWaitingList;
};

type GuestFlowStep = 'selection' | 'details' | 'actions';

type IxpoleAttendee = {
  firstName: string;
  lastName: string;
  email: string;
  ticketUuid?: string | null;
  ticketDescription?: string | null;
  deepLinkUrl?: string | null;
};

// The first attendee is the guest themselves; the others are other people (WCAG 1.3.5).
const PRIMARY_GUEST_AUTOCOMPLETE = {
  firstName: 'given-name',
  lastName: 'family-name',
  email: 'email',
} as const;

type GuestDetailsFormValues = {
  attendees: IxpoleAttendee[];
};

const createEmptyAttendee = (): IxpoleAttendee => ({
  firstName: '',
  lastName: '',
  email: '',
});

const getAttendeesFromWaitingList = (
  amount: number,
  waitingListData: GuestWaitingList,
  fallback: IxpoleAttendee[] = []
): IxpoleAttendee[] => {
  const existingAttendees = waitingListData.position?.attendeesInfo?.attendees;

  return Array.from({ length: amount }, (_, index) => {
    const existingAttendee = existingAttendees?.[index];
    const fallbackAttendee = fallback[index];

    return {
      firstName:
        existingAttendee?.firstName || fallbackAttendee?.firstName || '',
      lastName: existingAttendee?.lastName || fallbackAttendee?.lastName || '',
      email: existingAttendee?.email || fallbackAttendee?.email || '',
      ticketUuid:
        existingAttendee?.ticketUuid ?? fallbackAttendee?.ticketUuid ?? null,
      ticketDescription:
        existingAttendee?.ticketDescription ??
        fallbackAttendee?.ticketDescription ??
        null,
      deepLinkUrl:
        existingAttendee?.deepLinkUrl ?? fallbackAttendee?.deepLinkUrl ?? null,
    };
  });
};

const normalizeAttendees = (
  amount: number,
  attendees?: GuestDetailsFormValues['attendees']
) =>
  Array.from(
    { length: amount },
    (_, index) => attendees?.[index] || createEmptyAttendee()
  );

const toAttendeesInfoPayload = (
  attendees: IxpoleAttendee[]
): AttendeesInfoValidator => ({
  attendees: attendees.map(({ firstName, lastName, email }) => ({
    firstName,
    lastName,
    email,
  })) as unknown as AttendeesInfoValidator['attendees'],
});

export function IxpoleGuestFlow({
  fanGroupData,
  waitingListData,
}: IxpoleGuestFlowProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm<GuestDetailsFormValues>();

  const searchParams = new URLSearchParams(window.location.search);
  const code = searchParams.get('code') ?? '';
  const waitingListId = searchParams.get('waitingListId') ?? '';
  const lang = searchParams.get('lang') || 'en';

  const currentSeatCount = getCurrentSeatCount(waitingListData);
  const maxTickets = getMaxSelectableTickets(waitingListData);
  const initialSavedAttendees = getAttendeesFromWaitingList(
    currentSeatCount,
    waitingListData
  );
  const hasRegisteredAttendees = initialSavedAttendees.some(
    (attendee) => attendee.ticketUuid || attendee.deepLinkUrl
  );
  const [currentStep, setCurrentStep] = useState<GuestFlowStep>(
    hasRegisteredAttendees ? 'actions' : 'selection'
  );
  const [selectedTickets, setSelectedTickets] = useState(() =>
    getInitialSelectedTickets(waitingListData)
  );
  const [savedAttendees, setSavedAttendees] = useState<IxpoleAttendee[]>(
    initialSavedAttendees
  );
  const [hasDeclined, setHasDeclined] = useState(false);
  const [isSavingGuests, setIsSavingGuests] = useState(false);
  const [pendingGuestLinkIndex, setPendingGuestLinkIndex] = useState<
    number | null
  >(null);
  const [sentGuestLinks, setSentGuestLinks] = useState<Record<number, boolean>>(
    {}
  );

  const { mutateAsync: updateSeats, isPending: isUpdatingSeats } =
    useUpdateGuestWaitingListSeats(waitingListId, { code });
  const { mutateAsync: cancelWaitingList, isPending: isCancelling } =
    useGuestCancelWaitingList(waitingListId, { code });
  const { mutateAsync: sendGuestIxpoleTicketLink } =
    useSendGuestIxpoleTicketLink(waitingListId, { code });
  const { mutateAsync: updateGuestAttendeeInfo } = useUpdateGuestAttendeeInfo(
    waitingListId,
    { code }
  );
  const imageUrl =
    waitingListData.waitingListImageUrl ||
    waitingListData.eventImageUrl ||
    waitingListData.groupCoverImageUrl ||
    fanGroupData.coverImageUrl;
  const bannerUrl =
    fanGroupData.coverImageUrl || fanGroupData.backgroundImageUrl;
  const brandColors = getBrandColors(fanGroupData.color, '#fa541c');
  const primaryColor = brandColors.brand;
  const primaryButtonStyle = {
    background: primaryColor,
    borderColor: primaryColor,
    color: brandColors.onBrand,
  };

  const helpUrl = fanGroupData.helpLinks?.[lang] || t('helpdesk_url') || '';
  const isSelectionActionPending = isUpdatingSeats || isCancelling;

  const title =
    waitingListData.translatedExperienceName ||
    waitingListData.translatedEventName;

  const descriptionHtml =
    waitingListData.description?.[lang] ||
    waitingListData.translatedDescription ||
    waitingListData.translatedEventDescription ||
    '';

  const formattedDate = useMemo(() => {
    const locale = getLocale();
    return dayjs(waitingListData.eventStartDate)
      .locale(locale)
      .format('DD MMM HH:mm');
  }, [waitingListData.eventStartDate]);

  // Move focus to the new step's heading so keyboard and screen-reader users land on it (WCAG 2.4.3).
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const previousStep = useRef(currentStep);
  useEffect(() => {
    if (previousStep.current === currentStep) return;
    previousStep.current = currentStep;
    stepHeadingRef.current?.focus();
  }, [currentStep]);

  useEffect(() => {
    form.setFieldsValue({
      attendees: normalizeAttendees(selectedTickets, savedAttendees),
    });
  }, [form, savedAttendees, selectedTickets]);

  const decreaseTickets = () =>
    setSelectedTickets((current) => Math.max(1, current - 1));

  const increaseTickets = () =>
    setSelectedTickets((current) => Math.min(maxTickets, current + 1));

  const handleSelectionContinue = async () => {
    if (isSelectionActionPending) {
      return;
    }

    try {
      if (selectedTickets !== currentSeatCount) {
        await updateSeats({ nbrOfSeats: selectedTickets });
      }
      setCurrentStep('details');
    } catch (error) {
      notification.error({
        message: t('guest_app_ixpole_tickets_update_error'),
      });
    }
  };

  const handleDecline = async () => {
    if (isSelectionActionPending) {
      return;
    }

    try {
      await cancelWaitingList({ waitingListId });
      setHasDeclined(true);
      notification.success({
        message: t('booking_cancellation_notification_success'),
      });
    } catch (error) {
      notification.error({
        message: t('booking_cancellation_notification_error'),
      });
    }
  };

  const handleSaveGuests = async () => {
    let values: GuestDetailsFormValues;

    try {
      values = await form.validateFields();
    } catch (error) {
      return;
    }

    const attendees = normalizeAttendees(selectedTickets, values.attendees);
    setIsSavingGuests(true);

    try {
      const updatedWaitingList = await updateGuestAttendeeInfo({
        attendeeInfo: toAttendeesInfoPayload(attendees),
      });
      setSavedAttendees(
        getAttendeesFromWaitingList(
          selectedTickets,
          updatedWaitingList ?? waitingListData,
          attendees
        )
      );
      setCurrentStep('actions');
      notification.success({
        message: t('guest_app_ixpole_guests_saved_title'),
        description: t('guest_app_ixpole_guests_saved_description'),
      });
    } catch (error) {
      const saveError = error as Error;
      notification.error({
        message: t(getIxpoleGuestSaveErrorTranslationKey(saveError)),
      });
    } finally {
      setIsSavingGuests(false);
    }
  };

  const handlePrimaryAction = () => {
    const deepLinkUrl = savedAttendees[0]?.deepLinkUrl;

    if (!deepLinkUrl) {
      notification.error({
        message: t('guest_app_ixpole_primary_ticket_link_error'),
      });
      return;
    }

    window.location.assign(deepLinkUrl);
  };

  const handleSendGuestLink = async (guestIndex: number) => {
    if (pendingGuestLinkIndex !== null) {
      return;
    }

    const wasAlreadySent = !!sentGuestLinks[guestIndex];
    setPendingGuestLinkIndex(guestIndex);

    try {
      await sendGuestIxpoleTicketLink({ attendeeSlot: guestIndex });
      setSentGuestLinks((current) => ({
        ...current,
        [guestIndex]: true,
      }));
      notification.success({
        message: t(
          wasAlreadySent
            ? 'guest_app_ixpole_link_resent_title'
            : 'guest_app_ixpole_link_sent_title'
        ),
      });
    } catch (error) {
      notification.error({
        message: t('guest_app_ixpole_ticket_link_error'),
      });
    } finally {
      setPendingGuestLinkIndex(null);
    }
  };

  const renderSelectionStep = () => (
    <div className={styles.stepSection}>
      <div className={styles.stepIndicator}>
        {t('guest_app_ixpole_step_indicator', { current: 1, total: 3 })}
      </div>
      <h2 className={styles.stepTitle} ref={stepHeadingRef} tabIndex={-1}>
        {t('guest_app_questionForHowManyTickets')}
      </h2>
      <div className={styles.ticketPicker}>
        <Button
          aria-label={t('guest_app_ixpole_decrease_tickets_aria')}
          icon={<MinusOutlined />}
          onClick={decreaseTickets}
          shape="circle"
          size="large"
          disabled={selectedTickets <= 1 || isSelectionActionPending}
        />
        <div
          className={styles.countValue}
          role="status"
          aria-live="polite"
          data-testid="ixpoleTicketCount"
        >
          {selectedTickets}
        </div>
        <Button
          aria-label={t('guest_app_ixpole_increase_tickets_aria')}
          icon={<PlusOutlined />}
          onClick={increaseTickets}
          shape="circle"
          size="large"
          disabled={selectedTickets >= maxTickets || isSelectionActionPending}
        />
      </div>
      <p className={styles.hint}>
        {t(
          maxTickets > 1
            ? 'guest_app_ixpole_selection_limit_plural'
            : 'guest_app_ixpole_selection_limit_singular',
          { count: maxTickets }
        )}
      </p>
      <div className={styles.buttonRow}>
        <Button
          className={styles.selectionButton}
          type="primary"
          size="large"
          style={primaryButtonStyle}
          loading={isUpdatingSeats}
          disabled={isSelectionActionPending}
          onClick={handleSelectionContinue}
        >
          {t('guest_app_ixpole_confirm')}
        </Button>
        <Popconfirm
          title={t('guest_app_decline_confirm_title')}
          onConfirm={handleDecline}
          okText={t('guest_app_decline_confirm_ok')}
          cancelText={t('general_no')}
          cancelButtonProps={{ autoFocus: true }}
          disabled={isSelectionActionPending}
        >
          <Button
            className={styles.selectionButton}
            size="large"
            loading={isCancelling}
            disabled={isSelectionActionPending}
          >
            {t('guest_app_ixpole_cancel')}
          </Button>
        </Popconfirm>
      </div>
    </div>
  );

  const renderDetailsStep = () => (
    <div className={styles.stepSection}>
      <div className={styles.stepIndicator}>
        {t('guest_app_ixpole_step_indicator', { current: 2, total: 3 })}
      </div>
      <div className={styles.detailsHeader}>
        <h2 className={styles.detailsTitle} ref={stepHeadingRef} tabIndex={-1}>
          {t('guest_app_ixpole_attendee_details_intro')}
        </h2>
      </div>
      <Form form={form} layout="vertical">
        {Array.from({ length: selectedTickets }).map((_, index) => (
          <div className={styles.guestCard} key={index}>
            <h3 className={styles.guestCardTitle}>
              <UserOutlined aria-hidden />
              <span>
                {t('guest_app_ixpole_guest_label', { number: index + 1 })}
                {index === 0
                  ? ` ${t('guest_app_ixpole_primary_guest_suffix')}`
                  : ''}
              </span>
            </h3>
            <div className={styles.nameRow}>
              <Form.Item
                className={styles.nameField}
                label={t('guest_app_ixpole_first_name')}
                name={['attendees', index, 'firstName']}
                rules={[
                  {
                    required: true,
                    message: t('guest_app_ixpole_first_name_required'),
                  },
                ]}
              >
                <Input
                  placeholder={t('guest_app_ixpole_first_name')}
                  size="large"
                  autoComplete={
                    index === 0 ? PRIMARY_GUEST_AUTOCOMPLETE.firstName : 'off'
                  }
                />
              </Form.Item>
              <Form.Item
                className={styles.nameField}
                label={t('guest_app_ixpole_last_name')}
                name={['attendees', index, 'lastName']}
                rules={[
                  {
                    required: true,
                    message: t('guest_app_ixpole_last_name_required'),
                  },
                ]}
              >
                <Input
                  placeholder={t('guest_app_ixpole_last_name')}
                  size="large"
                  autoComplete={
                    index === 0 ? PRIMARY_GUEST_AUTOCOMPLETE.lastName : 'off'
                  }
                />
              </Form.Item>
            </div>
            <Form.Item
              label={t('guest_app_ixpole_email')}
              name={['attendees', index, 'email']}
              rules={[
                {
                  required: true,
                  message: t('guest_app_ixpole_email_required'),
                },
                {
                  type: 'email',
                  message: t('guest_app_ixpole_email_invalid'),
                },
              ]}
            >
              <Input
                placeholder={t('guest_app_ixpole_email_placeholder')}
                size="large"
                autoComplete={
                  index === 0 ? PRIMARY_GUEST_AUTOCOMPLETE.email : 'off'
                }
              />
            </Form.Item>
          </div>
        ))}
      </Form>
      <div className={styles.buttonRow}>
        <Button
          size="large"
          style={{ height: 44 }}
          onClick={() => setCurrentStep('selection')}
        >
          <LeftOutlined aria-hidden />
          {t('prev_btn_text')}
        </Button>
        <Button
          type="primary"
          size="large"
          style={{ ...primaryButtonStyle, height: 44 }}
          loading={isSavingGuests}
          onClick={handleSaveGuests}
        >
          {t('guest_app_ixpole_save_guests')}
        </Button>
      </div>
    </div>
  );

  const renderActionsStep = () => (
    <div className={styles.stepSection}>
      <div className={styles.stepIndicator}>
        {t('guest_app_ixpole_step_indicator', { current: 3, total: 3 })}
      </div>
      <div className={styles.detailsHeader}>
        <h2 className={styles.detailsTitle} ref={stepHeadingRef} tabIndex={-1}>
          {t('guest_app_ixpole_ticket_actions_intro')}
        </h2>
      </div>
      <div>
        {savedAttendees.map((attendee, index) => {
          const isPrimaryGuest = index === 0;
          const linkSent = !!sentGuestLinks[index];

          return (
            <div className={styles.actionGuestBlock} key={index}>
              <div className={styles.guestHeadingRow}>
                <h3 className={styles.guestHeading}>
                  {t('guest_app_ixpole_guest_label', { number: index + 1 })}
                </h3>
                {isPrimaryGuest ? (
                  <div className={styles.primaryBadge}>
                    {t('guest_app_ixpole_primary_guest_badge')}
                  </div>
                ) : null}
              </div>
              <dl className={styles.nameRow}>
                <div className={styles.readOnlyField}>
                  <dt className={styles.readOnlyLabel}>
                    {t('guest_app_ixpole_first_name')}
                  </dt>
                  <dd className={styles.readOnlyValue}>{attendee.firstName}</dd>
                </div>
                <div className={styles.readOnlyField}>
                  <dt className={styles.readOnlyLabel}>
                    {t('guest_app_ixpole_last_name')}
                  </dt>
                  <dd className={styles.readOnlyValue}>{attendee.lastName}</dd>
                </div>
              </dl>
              <div className={styles.emailActionRow}>
                <dl className={styles.emailField}>
                  <dt className={styles.readOnlyLabel}>
                    {t('guest_app_ixpole_email')}
                  </dt>
                  <dd className={styles.readOnlyValue}>{attendee.email}</dd>
                </dl>
                {isPrimaryGuest ? (
                  <Button
                    type="primary"
                    size="large"
                    className={styles.inlineActionButton}
                    style={{ ...primaryButtonStyle, maxHeight: 40 }}
                    icon={<DownloadOutlined aria-hidden />}
                    onClick={handlePrimaryAction}
                  >
                    {t('guest_app_ixpole_download_ticket')}
                  </Button>
                ) : (
                  <Button
                    size="large"
                    className={styles.inlineActionButton}
                    style={{ ...primaryButtonStyle, maxHeight: 40 }}
                    icon={<MailOutlined aria-hidden />}
                    loading={pendingGuestLinkIndex === index}
                    disabled={pendingGuestLinkIndex !== null}
                    onClick={() => void handleSendGuestLink(index)}
                  >
                    {t(
                      linkSent
                        ? 'guest_app_ixpole_resend_link'
                        : 'guest_app_ixpole_send_ticket_link'
                    )}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <Divider className={styles.calendarDivider} />
      <CalendarSelection waitingListData={waitingListData} />
    </div>
  );

  return (
    <div
      className={styles.page}
      style={
        {
          '--ixpole-primary-color': primaryColor,
          '--ixpole-primary-text-color': brandColors.text,
          '--ixpole-muted-color': baseColor600,
        } as React.CSSProperties
      }
    >
      <div className={styles.banner}>
        {bannerUrl ? (
          <img
            src={bannerUrl}
            alt={
              fanGroupData.translatedName ||
              fanGroupData.name.en ||
              t('guest_app_ixpole_banner_alt')
            }
            className={styles.bannerImage}
          />
        ) : (
          <div className={styles.bannerFallback}>
            <SeatersLogoLight className={styles.bannerFallbackLogo} />
          </div>
        )}
      </div>
      <div className={styles.card}>
        <div className={styles.section}>
          <div className={styles.summaryHeader}>
            <div>
              <h1 className={styles.summaryTitle}>{title}</h1>
            </div>
            <p className={styles.summaryLabel}>
              {t('guest_app_ixpole_vip_tickets')}
            </p>
          </div>
          <div className={styles.metaList}>
            <div className={styles.metaRow}>
              <CalendarOutlined aria-hidden />
              <span>{formattedDate}</span>
            </div>
            <div className={styles.metaRow}>
              <EnvironmentOutlined aria-hidden />
              <span>{waitingListData.translatedVenueName}</span>
            </div>
          </div>
        </div>
        <Divider style={{ margin: 0 }} />
        <div className={styles.section}>
          <img alt={title} className={styles.heroImage} src={imageUrl} />
        </div>
        <div className={styles.section}>
          <h2 className={styles.aboutTitle}>
            {t('guest_app_ixpole_about_experience')}
          </h2>
          <LexicalEditor
            borderless
            compact
            disabled
            placeholder=""
            toolbar={false}
            value={decodeAmpHtmlEntity(descriptionHtml)}
            className={styles.aboutEditor}
          />
        </div>
        <Divider style={{ margin: 0 }} />
        {hasDeclined ? (
          <div className={styles.section}>
            <Alert message={t('guest_app_waitingSeat')} type="info" showIcon />
          </div>
        ) : (
          <>
            {currentStep === 'selection' && renderSelectionStep()}
            {currentStep === 'details' && renderDetailsStep()}
            {currentStep === 'actions' && renderActionsStep()}
          </>
        )}
        <Divider style={{ margin: 0 }} />
        <div className={styles.section}>
          <div className={styles.contact}>
            {t('guest_app_ixpole_contact_prefix')}{' '}
            {helpUrl ? (
              <a
                className={styles.contactLink}
                href={helpUrl}
                rel="noreferrer"
                target="_blank"
              >
                {t('guest_app_ixpole_contact_link')}
              </a>
            ) : (
              <span className={styles.contactLink}>
                {t('guest_app_ixpole_contact_link')}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
